import { AiAssistantButton } from '@/components/GenAI/AiAssistantButton';
import { AiAssistantDrawer } from '@/components/GenAI/AiAssistantDrawer';
import { DATE_FORMAT } from '@/helpers/dates';
import { useGetDrawerMarginStyles } from '@/hooks/useGetDrawerMarginStyles';
import { useParentDivLayoutStyles } from '@/hooks/useParentDivLayoutStyles';
import { useEditAccountMutation } from '@/services/accounts';
import { useSubmitPtoPackageMutation } from '@/services/activationPackage';
import { useGetDocumentsQuery } from '@/services/documents';
import { isErrorWithData } from '@/services/helpers';
import { useGetQueueByAccountIdAndTypeQuery } from '@/services/queues';
import { Alert, Box, type BoxProps, Button, toast, useOpenClose } from '@palmetto/palmetto-components';
import { Formik, type FormikValues } from 'formik';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import { ActivationDocumentTypes, type IsoDateString, QueueStatus, QueueType, UserPermissions } from 'types';

import { useGetAccountQuery } from '../../services/accounts';
import { BaseRejectionReasons } from '../../types/BasePackageTypes';
import { ReviewPackageActions } from '../../types/ReviewPackage';
import { ProofOfPto } from '../ActivationMilestonePackage/ProofOfPto';
import { useAuth } from '../auth/authProvider';
import RequirePalmettoFinanceUser, { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import RequirePermissions from '../auth/requirePermissions';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';
import AdminStipulationBannerList from '../Stipulations/AdminStipulationBannerList';

const boxStyle: BoxProps = {
  direction: {
    base: 'column',
    tablet: 'row',
  },
  alignItems: {
    base: 'stretch',
    tablet: 'flex-end',
  },
  justifyContent: {
    tablet: 'flex-end',
  },
  childGap: 'sm',
  style: { flexShrink: 0 },
  position: 'relative',
  padding: 'lg 0 0 0',
};

const ptoSchema = yup.object({
  ptoGrantedDate: yup.string().required('PTO granted date is required'),
  qcCorrectedPtoDate: yup.string().when('isEpcPtoDateIncorrect', {
    is: true,
    then: (schema) => schema.required('QC Corrected PTO Date is required when updating the date'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export function AdvancePtoForm() {
  const { id: accountId, action = ReviewPackageActions.review } = useParams<{
    id: any;
    action: ReviewPackageActions;
  }>();
  const navigate = useNavigate();
  const { data: account } = useGetAccountQuery(accountId);
  const { data: ptoQueue } = useGetQueueByAccountIdAndTypeQuery({
    accountId,
    type: QueueType.permissionToOperate,
  });
  const [submit] = useSubmitPtoPackageMutation();
  const [editAccount] = useEditAccountMutation();
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);

  const targetRef = useRef(null);
  const {
    isOpen: isModelOutputsDrawerOpen,
    handleClose: closeModelOutputsDrawer,
    handleToggle: toggleModelOutputsDrawer,
  } = useOpenClose();
  const parentStyles = useParentDivLayoutStyles(targetRef);
  const drawerMarginStyles = useGetDrawerMarginStyles(parentStyles, isModelOutputsDrawerOpen, 'left');
  const { data: ptoDocuments } = useGetDocumentsQuery({
    accountId,
    documentTypeFilter: [ActivationDocumentTypes.ptoConfirmation],
  });
  const hasPtoGreenlight = account?.ptoGreenlight?.greenlightStatus ?? false;
  const showWarning = !hasPtoGreenlight && ptoQueue?.status !== QueueStatus.approved;
  const primaryApplicantName = account?.primaryApplicantName;
  const latestRejection = useMemo(() => {
    if (ptoQueue?.status !== QueueStatus.rejected || !ptoQueue.rejectionReasons?.length) {
      return undefined;
    }
    return [...ptoQueue.rejectionReasons].toSorted(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    )[0];
  }, [ptoQueue?.status, ptoQueue?.rejectionReasons]);
  const showRejectionAlert = !!latestRejection;
  const disableForm = isFinCoUser
    ? false
    : [
        QueueStatus.approved,
        QueueStatus.completeHoldForApproval,
        QueueStatus.submitted,
        QueueStatus.resubmitted,
      ].includes(ptoQueue?.status) ||
      (action !== ReviewPackageActions.edit && action !== ReviewPackageActions.review);

  const skip = useCallback(() => {
    navigate(`/accounts/${accountId}/activation-package/${ReviewPackageActions.create}`);
  }, [accountId, navigate]);

  const goBack = useCallback(() => {
    navigate(`/accounts/${accountId}`);
  }, [accountId, navigate]);

  const updatePtoDateInReview = useCallback(
    async (values: FormikValues) => {
      try {
        // Use the corrected date as the new ptoGrantedDate
        const accountUpdates: any = {
          ptoGrantedDate: DateTime.fromFormat(values.qcCorrectedPtoDate, DATE_FORMAT).toISODate() as IsoDateString,
          epcIncorrectSubmittedPtoGrantedDate: DateTime.fromFormat(
            values.epcIncorrectSubmittedPtoGrantedDate,
            DATE_FORMAT,
          ).toISODate() as IsoDateString,
        };

        await editAccount({
          id: accountId,
          account: accountUpdates,
        }).unwrap();
        toast.success('PTO date updated');
      } catch (e) {
        console.error(e);
        if (isErrorWithData(e)) {
          const errorMessage = e.data.message;
          toast.error(errorMessage);
        } else {
          toast.error('Error updating PTO date');
        }
      }
    },
    [accountId, editAccount],
  );

  const submitProofOfPto = useCallback(
    async (values: FormikValues) => {
      if (!values.ptoGrantedDate && ptoDocuments?.length === 0) {
        skip();
        return;
      }
      try {
        await submit({
          accountId,
          ptoPackage: {
            ptoGrantedDate:
              (DateTime.fromFormat(values.ptoGrantedDate, DATE_FORMAT).toISODate() as IsoDateString) ?? '',
          },
        }).unwrap();
        skip();
        toast.success('PTO data submitted');
      } catch (e) {
        console.error(e);
        if (isErrorWithData(e)) {
          const errorMessage = e.data.message;
          toast.error(errorMessage);
        } else {
          toast.error('Error submitting PTO data');
        }
      }
    },
    [accountId, ptoDocuments?.length, skip, submit],
  );

  return (
    <>
      <Helmet>
        <title>Proof of PTO</title>
      </Helmet>
      <AdminStipulationBannerList />
      <PageHeader
        eyebrow={<Link to={`/accounts/${accountId}`}>{primaryApplicantName}</Link>}
        title="Submit Proof of Permission to Operate"
      />
      <MainContainer>
        <Box
          ref={targetRef}
          style={{
            marginLeft: drawerMarginStyles,
            transition: 'margin-left 0.3s ease',
          }}
          gap="md"
        >
          <Formik
            initialValues={{
              ptoGrantedDate: account?.ptoGrantedDate
                ? DateTime.fromISO(account?.ptoGrantedDate).toFormat(DATE_FORMAT)
                : '',
              isEpcPtoDateIncorrect: false,
              qcCorrectedPtoDate: undefined,
              epcIncorrectSubmittedPtoGrantedDate: undefined,
            }}
            validationSchema={ptoDocuments && ptoDocuments.length > 0 ? ptoSchema : undefined}
            onSubmit={action === ReviewPackageActions.review ? updatePtoDateInReview : submitProofOfPto}
            enableReinitialize={true}
            validateOnChange={true}
          >
            {({ submitForm, isSubmitting, values }) => (
              <Box>
                {showRejectionAlert && (
                  <Alert
                    variant="danger"
                    hasIcon
                    title="Proof of PTO Rejected"
                    message={
                      <Box childGap="xs">
                        {!!latestRejection?.reasons?.length && (
                          <Box fontWeight="medium">
                            {latestRejection.reasons
                              .map(
                                (reason: string) =>
                                  BaseRejectionReasons[reason as keyof typeof BaseRejectionReasons] ?? reason,
                              )
                              .join(', ')}
                          </Box>
                        )}
                        {latestRejection?.note && <Box>{latestRejection.note}</Box>}
                      </Box>
                    }
                  />
                )}
                {showWarning && (
                  <Alert
                    variant="warning"
                    hasIcon
                    title="PTO Greenlight Not Granted"
                    message={
                      <Box childGap="md">
                        <Box>
                          Starting in Q2, LightReach reserves the right to net fund the tax credit amount from EPC
                          milestone payment if the job achieves PTO from the Utility before Greenlight is given by
                          LightReach.
                        </Box>
                        <Box>To submit other portions of the Activation Package, click the Next button below.</Box>
                      </Box>
                    }
                  />
                )}
                <ProofOfPto
                  showSubmit={!disableForm}
                  disabled={disableForm}
                  isReviewMode={action === ReviewPackageActions.review}
                />
                <Box
                  {...boxStyle}
                  width={{
                    base: '100',
                    tablet: 'auto',
                  }}
                >
                  <Button onClick={goBack} variant="secondary" tone="neutral" isLoading={isSubmitting}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={submitForm}
                    isLoading={isSubmitting}
                    isDisabled={action === ReviewPackageActions.review && !values.isEpcPtoDateIncorrect}
                    iconSuffix={action === ReviewPackageActions.review ? undefined : 'arrow-right'}
                    data-testid="frm-foot-check-cta"
                  >
                    {action === ReviewPackageActions.review ? 'Save' : 'Submit PTO'}
                  </Button>
                </Box>
              </Box>
            )}
          </Formik>
        </Box>
        <RequirePalmettoFinanceUser>
          <RequirePermissions permissions={[UserPermissions.lightReachQCTech]}>
            <AiAssistantButton
              toggleDrawer={toggleModelOutputsDrawer}
              accountId={accountId}
              isDrawerOpen={isModelOutputsDrawerOpen}
            />
            <AiAssistantDrawer
              accountId={accountId}
              queueId={ptoQueue?.id}
              isOpen={isModelOutputsDrawerOpen}
              onClose={closeModelOutputsDrawer}
            />
          </RequirePermissions>
        </RequirePalmettoFinanceUser>
      </MainContainer>
    </>
  );
}
